import {
  Avatar,
  Button,
  Divider,
  FormControl,
  Icon,
  MenuItem,
  Select,
  Stack,
  SwipeableDrawer,
  Typography,
} from "@mui/material";

import { useLogout } from "api";
import { useAppLayoutContext, useThemeModeContext } from "components/contexts";
import { ThemeMode } from "components/contexts/ThemeModeContext/ThemeModeContext";
import { useFormatMessage } from "lang";
import { useLocaleContext } from "lang/context/LocaleContext";
import { BOTTOM_NAV_HEIGHT } from "routes/consts";
import { getUser, useAppSelector } from "store";
import { isIOS, isPWAiOS } from "utils/detectDevice";

import { PowerPlantSelector, SidebarContent } from "./components";
import PWAInfo from "./components/PWAInfo";

export const Sidebar = () => {
  const formatMessage = useFormatMessage();
  const user = useAppSelector(getUser);
  const { sidebarOpen, setSidebarOpen } = useAppLayoutContext();
  const { themeMode, setThemeMode } = useThemeModeContext();
  const { locale, setLocale } = useLocaleContext();
  const logoutMutation = useLogout();

  const toggleDrawer = (open: boolean) => () => {
    setSidebarOpen(open);
  };

  const handleLogout = async (event: React.MouseEvent) => {
    event.preventDefault();
    await logoutMutation.mutateAsync();
  };

  return (
    <div>
      <SwipeableDrawer
        anchor="right"
        open={sidebarOpen}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        disableSwipeToOpen={isIOS() && !isPWAiOS()}
        sx={{
          "& .MuiDrawer-paper": {
            height: `calc(100dvh - ${BOTTOM_NAV_HEIGHT}px)`,
            bgcolor: "background.paper",
            backgroundImage: "none",
          },
        }}
      >
        <Stack
          sx={{
            width: 300,
            maxWidth: "70dvw",
            height: "100%",
          }}
        >
          <Stack direction="row" sx={{ px: 4, py: 2, gap: 2 }}>
            <Stack
              direction="row"
              sx={{ gap: 2, alignItems: "center", flexGrow: 1, py: 2 }}
            >
              <Avatar
                sizes="small"
                alt={`${user?.firstName} ${user?.lastName}`}
              >{`${user?.firstName[0]}${user?.lastName[0]}`}</Avatar>
              <Typography component="p" variant="h6">
                {`${user?.firstName} ${user?.lastName}`}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Stack sx={{ flexGrow: 1, overflow: "auto", "& > *": { flexShrink: 0 } }}>
            <SidebarContent />
            <PWAInfo />
            <Divider />
            <Stack
              direction="row"
              sx={{ px: 4, py: 2, gap: 2 }}
            >
            <FormControl size="small" sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {formatMessage("theme")}
              </Typography>
              <Select
                value={themeMode}
                onChange={(e) => setThemeMode(e.target.value as ThemeMode)}
                MenuProps={{ anchorOrigin: { vertical: "top", horizontal: "left" }, transformOrigin: { vertical: "bottom", horizontal: "left" } }}
                renderValue={(value) => (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Icon fontSize="small">
                      {value === "light"
                        ? "light_mode"
                        : value === "dark"
                          ? "dark_mode"
                          : "brightness_auto"}
                    </Icon>
                    {formatMessage(`theme.${value}`)}
                  </Stack>
                )}
              >
                <MenuItem value="light">
                  <Icon fontSize="small" sx={{ mr: 1 }}>
                    light_mode
                  </Icon>
                  {formatMessage("theme.light")}
                </MenuItem>
                <MenuItem value="auto">
                  <Icon fontSize="small" sx={{ mr: 1 }}>
                    brightness_auto
                  </Icon>
                  {formatMessage("theme.auto")}
                </MenuItem>
                <MenuItem value="dark">
                  <Icon fontSize="small" sx={{ mr: 1 }}>
                    dark_mode
                  </Icon>
                  {formatMessage("theme.dark")}
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {formatMessage("language")}
              </Typography>
              <Select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                MenuProps={{ anchorOrigin: { vertical: "top", horizontal: "left" }, transformOrigin: { vertical: "bottom", horizontal: "left" } }}
              >
                <MenuItem value="pl">Polski</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </Select>
            </FormControl>
            </Stack>
          </Stack>
          <PowerPlantSelector />
          <Stack sx={{ p: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<Icon>logout</Icon>}
              onClick={handleLogout}
              sx={{
                textTransform: "none",
              }}
            >
              {formatMessage("logout")}
            </Button>
          </Stack>
        </Stack>
      </SwipeableDrawer>
    </div>
  );
};
